import {
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied
} from '@mui/icons-material';
import { Card, Checkbox, FormControlLabel, FormGroup, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type FC, useState } from 'react';
import { AvaniButton } from '../Buttons';

type Survey = {
  alreadyDone: boolean;
  questions: {
    text: string;
    type: string;
    options?: {
      label: string;
      id: string;
    }[];
  }[];
};

interface AvaniFeedbackSurveyCardProps {
  survey: Survey;
}

const SurveyImpressions = [
  {
    label: 'Really Positive',
    icon: <SentimentVerySatisfied fontSize={'large'} />
  },
  {
    label: 'Positive',
    icon: <SentimentSatisfied fontSize={'large'} />
  },
  {
    label: 'It was alright',
    icon: <SentimentNeutral fontSize={'large'} />
  },
  {
    label: 'Negative',
    icon: <SentimentDissatisfied fontSize={'large'} />
  },
  {
    label: 'Really Negative',
    icon: <SentimentVeryDissatisfied fontSize={'large'} />
  }
];

const AvaniStyledCard = styled(Card)(
  ({ theme }) => `
  border-radius: 16px;
  padding: 16px;
  background-color: ${theme.colors.avani.lightTurc};
  height: 100%;

  & > .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

export const AvaniFeedbackSurveyCard: FC<AvaniFeedbackSurveyCardProps> = ({ survey }) => {
  const theme = useTheme();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [isComplete, setIsComplete] = useState(!!survey.alreadyDone);

  const submitAnswer = (): void => {
    if (currentScreen < survey.questions.length) {
      setCurrentScreen(currentScreen + 1);
    } else if (currentScreen === survey.questions.length) {
      //Send survey results
      //...
      setIsComplete(true);
      setCurrentScreen(currentScreen + 1);
    } else {
      setIsComplete(false);
      setCurrentScreen(1);
    }
  };

  return (
    <AvaniStyledCard raised={true}>
      <Typography
        variant={'h3'}
        sx={{
          color: theme.colors.avani.teal
        }}
      >
        Tell Us What You Think
      </Typography>
      <Typography variant={'h3'} fontSize={24} color={'primary.main'}>
        {isComplete
          ? [`We are Done!`, <br key={'0'} />, `Thank you for your feedback!`]
          : survey.questions[currentScreen - 1].text}
      </Typography>

      <Card
        elevation={0}
        sx={{
          borderRadius: '16px',
          width: '100%',
          marginBottom: '24px'
        }}
      >
        {!isComplete && survey.questions[currentScreen - 1].type === 'singleChoice' && (
          <div>
            <FormGroup
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                padding: '34px 34px',
                justifyContent: 'space-around'
              }}
            >
              {survey.questions[currentScreen - 1].options?.map((option, ind) => {
                return (
                  <FormControlLabel
                    key={option.id + ind}
                    label={option.label}
                    control={
                      <Checkbox
                        size={'large'}
                        sx={{
                          color: theme.colors.avani.turc,
                          '&.Mui-checked': {
                            color: theme.colors.avani.turc
                          }
                        }}
                      />
                    }
                    sx={{}}
                  />
                );
              })}
            </FormGroup>
          </div>
        )}
        {!isComplete && survey.questions[currentScreen - 1].type === 'sentiment' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              padding: '24px 0',
              justifyContent: 'space-around'
            }}
          >
            {SurveyImpressions.map((imp, i) => {
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    maxWidth: '80px'
                  }}
                >
                  {imp.icon}
                  <Typography
                    variant={'body1'}
                    fontWeight={'normal'}
                    fontSize={14}
                    lineHeight={1}
                    sx={{
                      marginTop: 1
                    }}
                  >
                    {imp.label}
                  </Typography>
                </div>
              );
            })}
          </div>
        )}
        {isComplete && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              padding: '38px 32px',
              justifyContent: 'space-around'
            }}
          >
            <div
              style={{
                marginRight: '24px'
              }}
            >
              <svg width="33" height="29" viewBox="0 0 33 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.6295 3.762C22.9646 2.08622 21.8754 0.455556 20.1996 0.1204C18.5238 -0.214756 16.8932 0.874502 16.558 2.55028L16.4098 3.28505C15.9844 5.42489 14.8113 7.34559 13.1098 8.712L12.6328 9.09228C11.1633 10.2653 10.3125 12.0442 10.3125 13.9263V15.5311V18.6249V21.0934C10.3125 23.1624 11.3437 25.096 13.0646 26.2433L15.5461 27.8997C16.5645 28.5765 17.7568 28.9374 18.9814 28.9374H25.2656C26.9736 28.9374 28.3594 27.5517 28.3594 25.8436C28.3594 25.6116 28.3336 25.3925 28.2885 25.1733C29.526 24.7673 30.4219 23.6071 30.4219 22.2343C30.4219 21.6477 30.2607 21.0999 29.9771 20.6294C31.408 20.3587 32.4844 19.1018 32.4844 17.5936C32.4844 16.6204 32.0332 15.7503 31.3307 15.1831C32.3232 14.6675 33 13.6298 33 12.4374C33 10.7294 31.6143 9.34364 29.9062 9.34364H20.625C21.508 7.85477 22.1396 6.21767 22.4812 4.49677L22.6295 3.762Z"
                  fill="#7FAAB2"
                />
                <path
                  d="M0 12.4375C0 11.2967 0.92168 10.375 2.0625 10.375H6.1875C7.32832 10.375 8.25 11.2967 8.25 12.4375V26.875C8.25 28.0158 7.32832 28.9375 6.1875 28.9375H2.0625C0.92168 28.9375 0 28.0158 0 26.875V12.4375Z"
                  fill="#005665"
                />
              </svg>
            </div>
            <Typography
              variant={'h3'}
              fontSize={24}
              sx={{
                flexGrow: 1
              }}
            >
              Your feedback helps us get better.
            </Typography>
          </div>
        )}
      </Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        {!isComplete && (
          <Typography
            fontSize={18}
            sx={{
              color: theme.colors.avani.teal
            }}
          >
            {currentScreen} / {survey.questions.length}
          </Typography>
        )}
        {isComplete && <div></div>}
        <div>
          {(currentScreen <= survey.questions.length || isComplete) && (
            <AvaniButton
              loading={false}
              label={currentScreen < survey.questions.length ? 'Next' : !isComplete ? 'Send' : 'Start Over'}
              onClick={() => submitAnswer()}
              sx={{
                mr: 0,
                lineHeight: 1,
                backgroundColor: 'transparent',
                color: theme.colors.avani.teal,
                border: `2px solid ${theme.colors.avani.teal}`,
                '&:hover': {
                  color: 'white',
                  backgroundColor: theme.colors.avani.teal,
                  border: `2px solid ${theme.colors.avani.teal}`
                }
              }}
            />
          )}
        </div>
      </div>
    </AvaniStyledCard>
  );
};
